import { useEffect, useRef, useCallback, useState } from 'react';
import * as d3 from 'd3';
import type { GraphNode, GraphEdge, EntityType } from '../../types';
import { exportToPNG, exportToSVG, exportToGraphML } from '../../utils/graphExport';
import { useGPU } from '../../hooks/useGPU';

interface ForceGraphProps {
  nodes: GraphNode[];
  edges: GraphEdge[];
  width?: number;
  height?: number;
  onNodeClick?: (node: GraphNode) => void;
  onNodeHover?: (node: GraphNode | null) => void;
  selectedNodeId?: string | null;
}

const ENTITY_COLORS: Record<EntityType, string> = {
  person: '#3b82f6',
  organization: '#a855f7',
  location: '#22c55e',
  vehicle: '#f97316',
  document: '#64748b',
  financial_account: '#eab308',
  phone_number: '#06b6d4',
  email: '#14b8a6',
  ip_address: '#ef4444',
  domain: '#f97316',
  cryptocurrency_wallet: '#f59e0b',
  event: '#8b5cf6',
};

interface SimNode extends d3.SimulationNodeDatum, GraphNode {}
interface SimEdge extends d3.SimulationLinkDatum<SimNode> {
  id: string;
  type: string;
  weight: number;
  confidence: number;
}

export default function ForceGraph({
  nodes,
  edges,
  width: propWidth,
  height: propHeight,
  onNodeClick,
  onNodeHover,
  selectedNodeId,
}: ForceGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const simulationRef = useRef<d3.Simulation<SimNode, SimEdge> | null>(null);
  const [gpuLayoutEnabled, setGpuLayoutEnabled] = useState(false);
  const [gpuLayoutRunning, setGpuLayoutRunning] = useState(false);
  const { engine: gpuEngine, isReady: gpuReady, backend: gpuBackend } = useGPU();

  const getNodeColor = useCallback((node: GraphNode) => {
    return ENTITY_COLORS[node.type] || '#64748b';
  }, []);

  const getNodeRadius = useCallback((node: GraphNode) => {
    const base = 6;
    const riskBonus = node.risk_score * 8;
    return base + riskBonus;
  }, []);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || nodes.length === 0) return;

    const container = containerRef.current;
    const width = propWidth || container.clientWidth;
    const height = propHeight || container.clientHeight;

    // Clear previous
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height]);

    // Defs for glow filter
    const defs = svg.append('defs');

    const filter = defs.append('filter').attr('id', 'glow');
    filter.append('feGaussianBlur').attr('stdDeviation', 3).attr('result', 'coloredBlur');
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Arrow marker
    defs
      .append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '-0 -5 10 10')
      .attr('refX', 20)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .append('svg:path')
      .attr('d', 'M 0,-5 L 10,0 L 0,5')
      .attr('fill', '#2d4a6f')
      .style('stroke', 'none');

    const g = svg.append('g');

    // Zoom
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 8])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    // Prepare data
    const simNodes: SimNode[] = nodes.map((n) => ({ ...n }));
    const nodeMap = new Map(simNodes.map((n) => [n.id, n]));

    const simEdges: SimEdge[] = edges
      .filter((e) => nodeMap.has(e.source) && nodeMap.has(e.target))
      .map((e) => ({
        ...e,
        source: e.source,
        target: e.target,
      }));

    // Simulation
    const simulation = d3
      .forceSimulation<SimNode>(simNodes)
      .force(
        'link',
        d3
          .forceLink<SimNode, SimEdge>(simEdges)
          .id((d) => d.id)
          .distance(80)
          .strength((d) => d.weight * 0.3)
      )
      .force('charge', d3.forceManyBody().strength(-200).distanceMax(300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius((d) => getNodeRadius(d as SimNode) + 4))
      .alphaDecay(0.02);

    simulationRef.current = simulation;

    // Edges
    const link = g
      .append('g')
      .selectAll<SVGLineElement, SimEdge>('line')
      .data(simEdges)
      .join('line')
      .attr('stroke', '#2d4a6f')
      .attr('stroke-opacity', (d) => 0.3 + d.confidence * 0.5)
      .attr('stroke-width', (d) => 1 + d.weight * 2)
      .attr('marker-end', 'url(#arrowhead)');

    // Nodes
    const node = g
      .append('g')
      .selectAll<SVGGElement, SimNode>('g')
      .data(simNodes)
      .join('g')
      .attr('cursor', 'pointer')
      .call(
        d3
          .drag<SVGGElement, SimNode>()
          .on('start', (event, d) => {
            if (!event.active) simulation.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
          })
          .on('drag', (event, d) => {
            d.fx = event.x;
            d.fy = event.y;
          })
          .on('end', (event, d) => {
            if (!event.active) simulation.alphaTarget(0);
            d.fx = null;
            d.fy = null;
          })
      );

    // Node circles
    node
      .append('circle')
      .attr('r', (d) => getNodeRadius(d))
      .attr('fill', (d) => getNodeColor(d))
      .attr('stroke', (d) => (d.id === selectedNodeId ? '#00f0ff' : getNodeColor(d)))
      .attr('stroke-width', (d) => (d.id === selectedNodeId ? 3 : 1.5))
      .attr('stroke-opacity', 0.6)
      .style('filter', (d) => (d.risk_score > 0.7 ? 'url(#glow)' : 'none'));

    // Node labels
    node
      .append('text')
      .text((d) => d.label.length > 15 ? d.label.substring(0, 15) + '...' : d.label)
      .attr('dy', (d) => getNodeRadius(d) + 12)
      .attr('text-anchor', 'middle')
      .attr('fill', '#94a3b8')
      .attr('font-size', '10px')
      .attr('font-family', 'Inter, sans-serif')
      .attr('pointer-events', 'none');

    // Interactions
    node
      .on('click', (_event, d) => onNodeClick?.(d))
      .on('mouseenter', (_event, d) => {
        onNodeHover?.(d);
        d3.select(_event.currentTarget)
          .select('circle')
          .transition()
          .duration(200)
          .attr('r', getNodeRadius(d) * 1.3)
          .attr('stroke-width', 3)
          .attr('stroke', '#00f0ff');
      })
      .on('mouseleave', (_event, d) => {
        onNodeHover?.(null);
        d3.select(_event.currentTarget)
          .select('circle')
          .transition()
          .duration(200)
          .attr('r', getNodeRadius(d))
          .attr('stroke-width', d.id === selectedNodeId ? 3 : 1.5)
          .attr('stroke', d.id === selectedNodeId ? '#00f0ff' : getNodeColor(d));
      });

    // Tooltip
    const tooltip = d3
      .select(container)
      .append('div')
      .attr('class', 'absolute pointer-events-none bg-surface-elevated border border-border-subtle rounded-lg px-3 py-2 text-xs shadow-xl z-50 hidden');

    node
      .on('mouseenter.tooltip', (event, d) => {
        tooltip
          .html(
            `<div class="font-semibold text-gray-200">${d.label}</div>
             <div class="text-gray-400 capitalize">${d.type.replace('_', ' ')}</div>
             <div class="mt-1 text-gray-400">Risk: <span class="text-cyber-amber font-mono">${Math.round(d.risk_score * 100)}%</span></div>`
          )
          .classed('hidden', false)
          .style('left', event.offsetX + 15 + 'px')
          .style('top', event.offsetY - 10 + 'px');
      })
      .on('mouseleave.tooltip', () => {
        tooltip.classed('hidden', true);
      });

    // Tick
    simulation.on('tick', () => {
      link
        .attr('x1', (d) => (d.source as SimNode).x!)
        .attr('y1', (d) => (d.source as SimNode).y!)
        .attr('x2', (d) => (d.target as SimNode).x!)
        .attr('y2', (d) => (d.target as SimNode).y!);

      node.attr('transform', (d) => `translate(${d.x},${d.y})`);
    });

    return () => {
      simulation.stop();
      tooltip.remove();
    };
  }, [nodes, edges, propWidth, propHeight, selectedNodeId, onNodeClick, onNodeHover, getNodeColor, getNodeRadius]);

  // GPU-accelerated layout
  useEffect(() => {
    if (!gpuLayoutEnabled || !gpuEngine || !svgRef.current || !containerRef.current || nodes.length === 0) return;

    setGpuLayoutRunning(true);

    const container = containerRef.current;
    const width = propWidth || container.clientWidth;
    const height = propHeight || container.clientHeight;

    // Build node/edge index maps
    const nodeIndexMap = new Map(nodes.map((n, i) => [n.id, i]));
    const gpuNodes = nodes.map((n) => ({
      id: n.id,
      mass: 1 + n.risk_score * 2,
    }));
    const gpuEdges = edges
      .filter((e) => nodeIndexMap.has(e.source) && nodeIndexMap.has(e.target))
      .map((e) => ({
        source: nodeIndexMap.get(e.source)!,
        target: nodeIndexMap.get(e.target)!,
      }));

    gpuEngine
      .computeForceLayout(gpuNodes, gpuEdges, 150)
      .then((positioned) => {
        if (!svgRef.current) return;

        // Apply GPU-computed positions to the D3 simulation
        const posMap = new Map(positioned.map((p) => [p.id, p]));
        const sim = simulationRef.current;
        if (sim) {
          sim.stop();
          const simNodes = sim.nodes();
          for (const sn of simNodes) {
            const pos = posMap.get(sn.id);
            if (pos) {
              // Center positions within viewport
              sn.x = pos.x + width / 2;
              sn.y = pos.y + height / 2;
              sn.vx = 0;
              sn.vy = 0;
            }
          }
          sim.alpha(0.01).restart();
        }
      })
      .catch((err) => {
        console.error('[ForceGraph] GPU layout failed:', err);
      })
      .finally(() => {
        setGpuLayoutRunning(false);
      });
  }, [gpuLayoutEnabled, gpuEngine, nodes, edges, propWidth, propHeight]);

  const handleExportPNG = useCallback(() => {
    if (svgRef.current) {
      exportToPNG(svgRef.current, 'helios-graph.png').catch(console.error);
    }
  }, []);

  const handleExportSVG = useCallback(() => {
    if (svgRef.current) {
      exportToSVG(svgRef.current, 'helios-graph.svg');
    }
  }, []);

  const handleExportGraphML = useCallback(() => {
    const exportNodes = nodes.map((n) => ({
      id: n.id,
      label: n.label,
      type: n.type,
    }));
    const exportEdges = edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      type: e.type,
      weight: e.weight,
    }));
    exportToGraphML(exportNodes, exportEdges, 'helios-graph.graphml');
  }, [nodes, edges]);

  return (
    <div ref={containerRef} className="relative w-full h-full min-h-[400px]">
      {/* Export toolbar */}
      <div className="absolute top-3 right-3 z-50 flex gap-1.5">
        {/* GPU Layout toggle */}
        {gpuReady && (
          <button
            onClick={() => setGpuLayoutEnabled(!gpuLayoutEnabled)}
            disabled={gpuLayoutRunning}
            className={`px-2.5 py-1.5 text-xs font-medium rounded border transition-colors flex items-center gap-1.5 ${
              gpuLayoutEnabled
                ? 'bg-cyber-cyan/10 text-cyber-cyan border-cyber-cyan/30'
                : 'bg-surface-elevated border-border-subtle text-gray-300 hover:bg-surface-hover hover:text-white'
            } ${gpuLayoutRunning ? 'opacity-60 cursor-wait' : ''}`}
            title={`GPU Layout (${gpuBackend?.toUpperCase()})`}
          >
            {gpuLayoutRunning ? (
              <div className="w-3 h-3 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
            ) : (
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25z" />
              </svg>
            )}
            GPU
          </button>
        )}
        {/* GPU backend badge */}
        {gpuReady && gpuLayoutEnabled && (
          <span className={`px-2 py-1.5 text-xs font-mono rounded border ${
            gpuBackend === 'webgpu'
              ? 'bg-green-500/10 text-green-400 border-green-500/30'
              : gpuBackend === 'webgl'
              ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'
              : 'bg-gray-500/10 text-gray-400 border-gray-500/30'
          }`}>
            {gpuBackend?.toUpperCase()}
          </span>
        )}
        <button
          onClick={handleExportPNG}
          className="px-2.5 py-1.5 text-xs font-medium rounded bg-surface-elevated border border-border-subtle text-gray-300 hover:bg-surface-hover hover:text-white transition-colors"
          title="Export as PNG"
        >
          PNG
        </button>
        <button
          onClick={handleExportSVG}
          className="px-2.5 py-1.5 text-xs font-medium rounded bg-surface-elevated border border-border-subtle text-gray-300 hover:bg-surface-hover hover:text-white transition-colors"
          title="Export as SVG"
        >
          SVG
        </button>
        <button
          onClick={handleExportGraphML}
          className="px-2.5 py-1.5 text-xs font-medium rounded bg-surface-elevated border border-border-subtle text-gray-300 hover:bg-surface-hover hover:text-white transition-colors"
          title="Export as GraphML"
        >
          GraphML
        </button>
      </div>
      <svg ref={svgRef} className="w-full h-full" />
    </div>
  );
}
